import { Controller } from "@hotwired/stimulus"

// The feature paywall modal (courses/limit_reached/_modal_shell). A locked Pro
// feature's link targets its turbo frame: the modal opens with a spinner as the
// request starts and shows the paywall once the frame loads. Closing empties the
// frame, so the next click fetches (and tracks) the paywall again.
export default class extends Controller {
    static targets = ["panel", "frame", "loading"]

    opening(event) {
        // Requests of the paywall's own forms (the feedback discount) bubble up here too.
        if (event.target !== this.frameTarget) return

        if (!this.isOpen) {
            this.trigger = document.activeElement
            // The Ask AI modal of the results page may already hold the scroll lock.
            this.lockedScroll = !document.body.classList.contains("overflow-hidden")
            if (this.lockedScroll) document.body.classList.add("overflow-hidden")
            this.element.classList.remove("hidden")
        }
        this.loadingTarget.classList.remove("hidden")
    }

    loaded(event) {
        if (event.target !== this.frameTarget) return

        this.loadingTarget.classList.add("hidden")
        // Anything but the paywall (a pro account sent home, a page without a feature
        // pitch) is a page of its own: go there.
        if (!this.frameTarget.querySelector("[data-paywall-modal-content]")) {
            const url = this.frameTarget.src
            this.close()
            if (url) Turbo.visit(url)
            return
        }
        this.panelTarget.focus({ preventScroll: true })
    }

    // A response without the paywall frame (signed out, sent to onboarding, an error
    // page) is a page of its own: Turbo would show "Content missing" in the modal.
    // The visit replaces the page, modal and all; closing first would drop the frame's
    // src and abort the response the visit still has to read.
    missing(event) {
        if (event.target !== this.frameTarget) return

        event.preventDefault()
        event.detail.visit(event.detail.response)
    }

    failed(event) {
        if (event.target === this.frameTarget) this.close()
    }

    closeOnEscape(event) {
        if (!this.isOpen) return

        // Only the paywall closes, not the Ask AI modal it may sit on.
        event.stopPropagation()
        this.close()
    }

    close() {
        if (!this.isOpen) return

        this.element.classList.add("hidden")
        this.loadingTarget.classList.add("hidden")
        this.frameTarget.removeAttribute("src")
        this.frameTarget.innerHTML = ""
        if (this.lockedScroll) document.body.classList.remove("overflow-hidden")
        this.lockedScroll = false
        this.trigger?.focus?.({ preventScroll: true })
        this.trigger = null
    }

    disconnect() {
        if (this.lockedScroll) document.body.classList.remove("overflow-hidden")
    }

    get isOpen() {
        return !this.element.classList.contains("hidden")
    }
}
